import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { formatDate, formatWeekdayDayMonth } from '../../../utils/formatters';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import useNotifications from '../../../hooks/useNotifications';
import AccessDenied from '../../../components/AccessDenied';
import SpeedDialFAB from '../../../components/SpeedDialFAB';
import SendInvoiceModal from '../../../components/SendInvoiceModal';
import ApproveServiceLogModal from '../../../components/ApproveServiceLogModal';
import ServiceLogViewModal from '../../../components/ServiceLogViewModal';
import CertSubmissionViewModal from '../../../components/CertSubmissionViewModal';
import InvoiceManager from './InvoiceManager';
import RewardSpeedDial from '../../app/components/RewardSpeedDial';
import MobileCreateVoucherModal from '../../app/components/MobileCreateVoucherModal';
import MobileReferralShareModal from '../../app/components/MobileReferralShareModal';

// Format date to YYYY-MM-DD using LOCAL time (not UTC)
function formatLocalDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

interface WorkflowInvoice {
  id: string;
  invoice_number: string;
  status: string;
  total: number;
  balance_due: number;
  referral_credit_applied?: number;
}

interface WorkflowItem {
  id: string;
  status: string;
  service_date: string;
  total_amount: number;
  total: number;
  customer_name: string;
  customer_email: string;
  email_bouncing?: boolean;
  email_last_bounce_at?: string | null;
  customer: { customer_name: string; email: string } | null;
  invoice: WorkflowInvoice | null;
  invoice_id?: string;
  internal_notes?: string;
  additional_cc_emails?: string;
  split_code?: { id: string; code: string; description: string } | null;
  created_at: string;
  [key: string]: unknown;
}

interface CertItem {
  id: string;
  service_date: string;
  operator_id: string;
  service_location: string;
  submission_status: string;
  created_at: string;
  invoice_number: string;
  customer_name: string;
  operator_name: string;
  display_customer: string;
  [key: string]: unknown;
}

interface BookingItem {
  id: string;
  booking_code: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  service_address?: string;
  service_date: string;
  service_time?: string;
  status: string;
  services?: unknown[];
  territory_id?: string;
  reference?: string;
  email_bouncing?: boolean;
  email_last_bounce_at?: string | null;
}

interface SendInvoiceData {
  id: string;
  invoice_number: string;
  customer_name: string;
  customer_email: string;
  customer_company?: string;
  total: number;
  due_date?: string;
  status: string;
  territory_id: string;
  referral_discount_applied?: number;
  referral_credit_applied?: number;
  additional_cc_emails?: string;
  [key: string]: unknown;
}

/**
 * UserHome — Main dashboard with workflow actions, quick actions, and actionable items
 * Cloned from Global UserHome — excluding Customer Engagement (added later)
 */
function UserHome() {
  const navigate = useNavigate();
  const { supabase, user, appMetadata, hasPermission } = useAuth();
  const { formatCurrency } = useConfig();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('dashboard');
  const isSuperAdmin = appMetadata?.is_super_admin === true;
  const userScope = appMetadata?.scope ?? 'own';

  // Permissions — Sentinel uses view|contribute|manage levels
  const canVerify = hasPermission('certification', 'manage');
  // Authorise is restricted to super-admins and country admins (global scope)
  const canAuthorize = isSuperAdmin || userScope === 'global';
  const canCreateBooking = hasPermission('bookings', 'contribute');
  const canViewBookings = hasPermission('bookings', 'view');
  const canAssignBookings = hasPermission('bookings', 'manage');
  const canCreateVoucher = hasPermission('vouchers', 'contribute');
  // Sharing an existing referral code is view-level (codes are auto-created);
  // creating a voucher stays at contribute. Operators get share, not create.
  const canShareReferral = hasPermission('referrals', 'view');
  const canAccessRewards = canCreateVoucher || canShareReferral;
  // Operators have no Rewards dashboard, so the View tile sends them to the
  // Codes list they can open rather than the gated dashboard.
  const rewardsViewPath = hasPermission('rewards_dashboard', 'view') ? '/rewards' : '/rewards/codes';

  // Notifications
  const {
    notifications,
    dismissNotification,
    voteOnPoll,
    hasVoted,
    getUserVote,
    getPollResults,
    loadPollResults,
    loading: notificationsLoading,
  } = useNotifications();
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);

  // Load poll results for notifications that have polls
  useEffect(() => {
    notifications.forEach(n => {
      if (n.poll_options?.length && hasVoted(n.id)) {
        loadPollResults(n.id);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length]);

  // Service Logs workflow state
  const [serviceLogsData, setServiceLogsData] = useState<{
    pending: WorkflowItem[];
    draft: WorkflowItem[];
    loading: boolean;
  }>({ pending: [], draft: [], loading: true });

  // Certification workflow state
  const [certificationData, setCertificationData] = useState<{
    pendingVerification: CertItem[];
    pendingAuthorization: CertItem[];
    loading: boolean;
  }>({ pendingVerification: [], pendingAuthorization: [], loading: true });

  // Bookings workflow state
  const [bookingsData, setBookingsData] = useState<{
    unassignedBookings: BookingItem[];
    loading: boolean;
  }>({ unassignedBookings: [], loading: true });

  // Send Invoice Modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState<SendInvoiceData | null>(null);

  // Approve Service Log Modal state — see ApproveServiceLogModal.tsx.
  const [approvingLog, setApprovingLog] = useState<WorkflowItem | null>(null);

  // View modal state
  const [viewingServiceLog, setViewingServiceLog] = useState<WorkflowItem | null>(null);
  const [viewingCertSubmission, setViewingCertSubmission] = useState<CertItem | null>(null);

  // Invoice Manager state
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState<string | null>(null);

  // Quick Actions modal state
  const [showSpeedDial, setShowSpeedDial] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Processing state
  const [processingItemId, setProcessingItemId] = useState<string | null>(null);

  // Success message toast
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  // Permissions for service logs
  const canEditServiceLogs = hasPermission('service_logs', 'contribute');
  const canApproveServiceLogs = hasPermission('service_logs', 'manage') || hasPermission('invoices', 'manage');
  const canSendInvoice = hasPermission('invoices', 'manage');

  // Load all workflow data on mount
  useEffect(() => {
    loadServiceLogsData();
    loadCertificationData();
    if (canViewBookings) {
      loadBookingsData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [canViewBookings]);

  // Load service logs workflow items
  const loadServiceLogsData = async () => {
    try {
      // Sentinel: invoices have service_log_id FK (reverse of Global's service_logs.invoice_id)
      const { data: logs } = await supabase
        .from('service_logs')
        .select(`
          *,
          customer:customers(id, customer_name, email, phone, email_bouncing, email_last_bounce_at),
          invoices!invoices_service_log_id_fkey(id, invoice_number, status, total, balance_due, vat_amount, referral_credit_applied),
          split_code:split_codes(id, code, description)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      const pending: WorkflowItem[] = [];
      const draft: WorkflowItem[] = [];

      (logs || []).forEach((rawLog: Record<string, unknown>) => {
        // Sentinel returns invoices as array (reverse FK), pick first
        const invoicesArr = rawLog.invoices as WorkflowInvoice[] | null;
        const invoice = invoicesArr?.[0] || null;
        // Map joined customer fields onto the log for display
        const cust = rawLog.customer as { customer_name: string; email: string; phone: string; email_bouncing?: boolean; email_last_bounce_at?: string | null } | null;
        const log = {
          ...rawLog,
          customer_name: (rawLog.customer_name as string) || cust?.customer_name || '',
          customer_email: (rawLog.customer_email as string) || cust?.email || '',
          customer_phone: (rawLog.customer_phone as string) || cust?.phone || '',
          customer: cust ? { customer_name: cust.customer_name, email: cust.email } : null,
          email_bouncing: cust?.email_bouncing === true,
          email_last_bounce_at: cust?.email_last_bounce_at ?? null,
          invoice,
          invoice_id: invoice?.id,
        } as unknown as WorkflowItem;
        const logStatus = (log.status || '').toLowerCase();
        const invoiceStatus = (invoice?.status || '').toLowerCase();
        const hasInvoice = !!invoice;

        if (hasInvoice && invoiceStatus === 'draft') {
          draft.push(log);
        } else if (!hasInvoice && !['sent', 'paid', 'overdue', 'cancelled'].includes(logStatus)) {
          pending.push(log);
        }
      });

      setServiceLogsData({
        pending: pending.slice(0, 10),
        draft: draft.slice(0, 10),
        loading: false
      });
    } catch (err) {
      console.error('Error loading service logs data:', err);
      setServiceLogsData(prev => ({ ...prev, loading: false }));
    }
  };

  // Load certification workflow items
  const loadCertificationData = async () => {
    try {
      const { data: submissions } = await supabase
        .from('cert_submissions')
        .select(`
          id,
          service_date,
          operator_id,
          service_location,
          submission_status,
          created_at,
          invoice_number,
          customer_name
        `)
        .in('submission_status', ['pending_verification', 'pending_authorization'])
        .order('created_at', { ascending: false })
        .limit(20);

      const operatorIds = [...new Set((submissions || []).map((s: { operator_id: string }) => s.operator_id).filter(Boolean))];
      const operatorMap: Record<string, string> = {};
      if (operatorIds.length > 0) {
        const { data: operators } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', operatorIds);
        (operators || []).forEach((op: { id: string; first_name: string; last_name: string }) => {
          operatorMap[op.id] = `${op.first_name} ${op.last_name}`.trim();
        });
      }

      const enriched: CertItem[] = (submissions || []).map((sub: {
        id: string;
        service_date: string;
        operator_id: string;
        service_location: string;
        submission_status: string;
        created_at: string;
        invoice_number: string;
        customer_name: string;
      }) => ({
        ...sub,
        operator_name: operatorMap[sub.operator_id] || 'Unknown',
        display_customer: sub.customer_name || sub.service_location?.split(' - ')[0] || '—'
      }));

      setCertificationData({
        pendingVerification: enriched.filter(s => s.submission_status === 'pending_verification').slice(0, 10),
        pendingAuthorization: enriched.filter(s => s.submission_status === 'pending_authorization').slice(0, 10),
        loading: false
      });
    } catch (err) {
      console.error('Error loading certification data:', err);
      setCertificationData(prev => ({ ...prev, loading: false }));
    }
  };

  // Load bookings — only unassigned (needs action)
  const loadBookingsData = async () => {
    try {
      const today = new Date();
      const todayStr = formatLocalDate(today);

      let unassignedBookings: BookingItem[] = [];

      if (canAssignBookings) {
        const { data: userTerritories } = await supabase
          .from('user_territories')
          .select('territory_id')
          .eq('user_id', user?.id);

        const territoryIds = (userTerritories || []).map((ut: { territory_id: string }) => ut.territory_id);

        if (territoryIds.length > 0 || isSuperAdmin) {
          let query = supabase
            .from('bookings')
            .select('id, booking_code, customer_name, customer_email, customer_phone, service_address, service_date, service_time, status, services, territory_id, customer:customers(email_bouncing, email_last_bounce_at)')
            .in('status', ['pending', 'confirmed'])
            .gte('service_date', todayStr)
            .order('service_date', { ascending: true })
            .limit(50);

          if (territoryIds.length > 0) {
            query = query.in('territory_id', territoryIds);
          } else {
            setBookingsData({ unassignedBookings: [], loading: false });
            return;
          }

          const { data: bookings } = await query;

          if (bookings && bookings.length > 0) {
            // Check which bookings have assignments
            const bookingIds = bookings.map((b: { id: string }) => b.id);
            const { data: assignments } = await supabase
              .from('booking_assignments')
              .select('booking_id')
              .in('booking_id', bookingIds);

            const assignedIds = new Set((assignments || []).map((a: { booking_id: string }) => a.booking_id));

            unassignedBookings = (bookings as Array<BookingItem & { customer?: { email_bouncing?: boolean; email_last_bounce_at?: string | null } | null }>)
              .filter(b => !assignedIds.has(b.id))
              .map(b => ({
                ...b,
                reference: b.booking_code,
                email_bouncing: b.customer?.email_bouncing === true,
                email_last_bounce_at: b.customer?.email_last_bounce_at ?? null,
              }))
              .slice(0, 10);
          }
        }
      }

      setBookingsData({ unassignedBookings, loading: false });
    } catch (err) {
      console.error('Error loading bookings data:', err);
      setBookingsData(prev => ({ ...prev, loading: false }));
    }
  };

  // View service log modal
  const handleViewServiceLog = async (log: WorkflowItem) => {
    try {
      const { data: fullLog, error } = await supabase
        .from('service_logs')
        .select('*, customer:customers(id, customer_name, email, phone), items:service_log_items(*), split_code:split_codes(id, code, description)')
        .eq('id', log.id)
        .single();
      if (!error && fullLog) {
        const cust = fullLog.customer as { customer_name: string; email: string; phone: string } | null;
        setViewingServiceLog({
          ...log,
          ...fullLog,
          customer_name: (fullLog.customer_name as string) || cust?.customer_name || log.customer_name,
          customer_email: (fullLog.customer_email as string) || cust?.email || log.customer_email,
          customer_phone: (fullLog.customer_phone as string) || cust?.phone || log.customer_phone,
        });
      } else {
        setViewingServiceLog(log);
      }
    } catch {
      setViewingServiceLog(log);
    }
  };

  // View cert submission modal
  const handleViewCertSubmission = async (submission: CertItem) => {
    try {
      const { data: full, error } = await supabase
        .from('cert_submissions')
        .select('*')
        .eq('id', submission.id)
        .single();
      if (!error && full) {
        setViewingCertSubmission({ ...submission, ...full });
      } else {
        setViewingCertSubmission(submission);
      }
    } catch {
      setViewingCertSubmission(submission);
    }
  };

  // Edit service log or invoice
  const handleEditServiceLog = (log: WorkflowItem) => {
    if (log.invoice?.invoice_number) {
      setViewingServiceLog(null);
      setEditingInvoiceNumber(log.invoice.invoice_number);
    } else {
      navigate(`/service-logs/${log.id}?returnTo=/`);
    }
  };

  const handleCloseInvoiceManager = () => {
    setEditingInvoiceNumber(null);
    loadServiceLogsData();
  };

  // Approve service log — opens the rich approve modal. The actual
  // invoice creation runs in executeApproveServiceLog once the approver
  // has confirmed recipient, CCs, payment terms, and notes.
  const handleApproveServiceLog = (log: WorkflowItem) => {
    if (!canApproveServiceLogs) return;
    setApprovingLog(log);
  };

  const executeApproveServiceLog = async (
    log: WorkflowItem,
    overrides: {
      recipient_email: string | null;
      additional_cc_emails: string | null;
      notes: string | null;
      payment_terms_days: number | null;
    },
  ): Promise<void> => {
    setProcessingItemId(log.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=create_from_service_log`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ service_log_id: log.id, ...overrides }),
        },
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        // Already invoiced (usually a retry after the first click landed) is not
        // a failure: refresh so it shows, rather than an error.
        if (response.status === 409 && result.already_invoiced) {
          setApprovingLog(null);
          await loadServiceLogsData();
          showSuccess(result.error || 'This service log is already invoiced.');
          return;
        }
        throw new Error(result.error || 'Failed to create invoice');
      }

      const invoiceNumber = result.invoice?.invoice_number || 'Draft';
      showSuccess(`Invoice ${invoiceNumber} created for ${log.customer_name}`);
      setApprovingLog(null);
      await loadServiceLogsData();
    } catch (err) {
      console.error('Error creating invoice:', err);
      alert('Error: ' + (err instanceof Error ? err.message : 'Unknown error'));
      throw err;
    } finally {
      setProcessingItemId(null);
    }
  };

  // Send invoice — read additional_cc_emails from the invoice itself
  // (now that the column exists). The legacy fallback to log.additional_cc_emails
  // is harmless and covers in-flight UI state.
  const handleSendInvoice = async (log: WorkflowItem) => {
    if (!canSendInvoice) return;
    const invoiceId = log.invoice_id || log.invoice?.id;
    if (!invoiceId) { alert('No invoice to send'); return; }

    try {
      const { data: invoice, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, customer_name, customer_email, customer_company, total, due_date, status, territory_id, referral_discount_applied, referral_credit_applied, additional_cc_emails')
        .eq('id', invoiceId)
        .single();
      if (error) throw error;

      const inv = invoice as Record<string, unknown>;
      setSendingInvoice({
        ...invoice,
        additional_cc_emails: (inv.additional_cc_emails as string | null) || log.additional_cc_emails || '',
      } as SendInvoiceData);
      setShowSendModal(true);
    } catch (err) {
      console.error('Error loading invoice:', err);
      alert('Error loading invoice: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleSendSuccess = (message: string) => {
    showSuccess(message || 'Invoice sent successfully!');
    loadServiceLogsData();
  };

  // Verify certification
  const handleVerifySubmission = async (submission: CertItem) => {
    if (!canVerify) return;
    if (!confirm(`Verify submission for ${submission.display_customer}?`)) return;

    try {
      const { error } = await supabase
        .from('cert_submissions')
        .update({
          submission_status: 'pending_authorization',
          verified_by: user?.id,
          verified_at: new Date().toISOString()
        })
        .eq('id', submission.id);
      if (error) throw error;
      showSuccess(`Verified: ${submission.display_customer}`);
      await loadCertificationData();
    } catch (err) {
      console.error('Error verifying submission:', err);
      alert('Error: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Authorise certification — server enforces super-admin / global-scope role
  // and same-person guard via the authorize_cert_submission RPC.
  const handleAuthoriseSubmission = async (submission: CertItem) => {
    if (!canAuthorize) return;
    if (!confirm(`Authorise certificate for ${submission.display_customer}?`)) return;

    try {
      const { error } = await supabase.rpc('authorize_cert_submission', { p_submission_id: submission.id });
      if (error) throw error;
      showSuccess(`Certificate authorised: ${submission.display_customer}`);
      await loadCertificationData();
    } catch (err) {
      console.error('Error authorising submission:', err);
      alert('Error: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Counts
  // Super admins don't action the approval step (managers do) — they only
  // care about "ready to send" on the dashboard. So pending logs are
  // excluded from their badge count and the section below.
  const showPendingApprovals = canApproveServiceLogs && !isSuperAdmin;
  const serviceLogsCount =
    (showPendingApprovals ? serviceLogsData.pending.length : 0) +
    (canSendInvoice ? serviceLogsData.draft.length : 0);
  const certificationCount =
    (canVerify ? certificationData.pendingVerification.length : 0) +
    (canAuthorize ? certificationData.pendingAuthorization.length : 0);
  const bookingsCount = canAssignBookings ? bookingsData.unassignedBookings.length : 0;

  // Format booking time — Sentinel uses TEXT (HH:MM) for service_time
  const formatBookingTime = (booking: BookingItem) => {
    if (!booking.service_time) return '';
    const parts = booking.service_time.split(':');
    const hour = parseInt(parts[0] ?? '0', 10);
    const minutes = parts[1] ?? '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatBookingDate = (booking: BookingItem) => {
    if (!booking.service_date) return '';
    const date = new Date(booking.service_date);
    return formatWeekdayDayMonth(date);
  };

  if (permissionLoading) {
    return (
      <div className="placeholder-page">
        <div className="spinner" />
      </div>
    );
  }

  if (!canAccess) {
    return <AccessDenied feature="dashboard" />;
  }

  return (
    <div className="dashboard-home">
      {/* Success Toast */}
      {successMessage && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem',
          background: 'var(--color-success)', color: 'white',
          padding: '0.75rem 1.25rem', borderRadius: 'var(--radius)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {successMessage}
        </div>
      )}

      {/* Portal Notifications */}
      {notifications.length > 0 && (
        <DashboardSection
          icon="📢" title="Notifications" count={notifications.length}
          accentColor="var(--color-primary)" loading={notificationsLoading}
        >
          <div className="flex flex-col gap-md">
            {(notificationsExpanded ? notifications : notifications.slice(0, 5)).map(notification => {
              const voted = hasVoted(notification.id);
              const userVote = getUserVote(notification.id);
              const results = getPollResults(notification.id);
              const iconMap: Record<string, string> = {
                info: 'ℹ️', warning: '⚠️', success: '✅', alert: '🚨', maintenance: '🔧',
              };
              const notifIcon = notification.icon || iconMap[notification.type] || 'ℹ️';

              return (
                <div key={notification.id} className="notification-card" style={{
                  background: 'var(--color-surface, #fff)',
                  border: '1px solid var(--color-border, #e5e7eb)',
                  borderRadius: 'var(--radius)',
                  padding: '0.75rem 1rem',
                  position: 'relative',
                }}>
                  {notification.is_dismissible && (
                    <button
                      onClick={() => dismissNotification(notification.id)}
                      style={{
                        position: 'absolute', top: '0.5rem', right: '0.5rem',
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 'var(--text-2xl)', color: 'var(--color-text-muted, #9ca3af)',
                        padding: '2px 6px', borderRadius: 'var(--radius-sm)',
                      }}
                      title="Dismiss"
                    >
                      &times;
                    </button>
                  )}
                  <div className="flex gap-sm items-start">
                    <span style={{ fontSize: 'var(--text-3xl)', flexShrink: 0 }}>{notifIcon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 'var(--text-base)', marginBottom: '0.25rem' }}>
                        {notification.title}
                      </div>
                      <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-secondary, #6b7280)', whiteSpace: 'pre-line' }}>
                        {notification.message}
                      </div>

                      {/* Poll */}
                      {notification.poll_options && notification.poll_options.length > 0 && (
                        <div className="mt-sm">
                          {!voted ? (
                            <div className="flex gap-sm flex-wrap">
                              {notification.poll_options.map((opt: { label: string; emoji: string }, idx: number) => (
                                <button
                                  key={idx}
                                  onClick={() => voteOnPoll(notification.id, idx)}
                                  className="btn btn-secondary"
                                  style={{ fontSize: 'var(--text-md)', padding: '0.25rem 0.75rem' }}
                                >
                                  {opt.emoji} {opt.label}
                                </button>
                              ))}
                            </div>
                          ) : results ? (
                            <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-muted, #9ca3af)' }}>
                              {notification.poll_options.map((opt: { label: string; emoji: string }, idx: number) => (
                                <span key={idx} style={{
                                  marginRight: '0.75rem',
                                  fontWeight: userVote === idx ? 600 : 400,
                                }}>
                                  {opt.emoji} {opt.label}: {results.counts[idx] || 0}
                                  {userVote === idx && ' (you)'}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-muted, #9ca3af)' }}>
                              Vote recorded
                            </div>
                          )}
                        </div>
                      )}

                      {/* Link */}
                      {notification.link_url && (
                        <div className="mt-sm">
                          <a
                            href={notification.link_url}
                            target={notification.link_url.startsWith('http') ? '_blank' : undefined}
                            rel={notification.link_url.startsWith('http') ? 'noopener noreferrer' : undefined}
                            style={{ fontSize: 'var(--text-md)', color: 'var(--color-primary, #2563eb)' }}
                          >
                            {notification.link_text || 'Learn more'}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {notifications.length > 5 && (
              <button
                onClick={() => setNotificationsExpanded(!notificationsExpanded)}
                className="action-group-more"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', textAlign: 'left' }}
              >
                {notificationsExpanded ? 'Show less' : `+${notifications.length - 5} more`}
              </button>
            )}
          </div>
        </DashboardSection>
      )}

      {/* Bookings Section — hidden for super admins (too noisy) and when empty */}
      {!isSuperAdmin && canViewBookings && bookingsCount > 0 && (
        <DashboardSection
          icon="📅" title="Bookings" count={bookingsCount}
          accentColor="var(--color-primary)" loading={bookingsData.loading}
          headerAction={<Link to="/bookings" className="dashboard-section-link">View All</Link>}
        >
          {bookingsData.unassignedBookings.length > 0 && canAssignBookings && (
            <div className="action-group" style={{ borderLeftColor: 'var(--color-warning)' }}>
              <div className="action-group-header">
                <div>
                  <span className="action-group-title">
                    Needs Assignment
                    <span className="action-group-badge" style={{ background: 'var(--color-warning)' }}>
                      {bookingsData.unassignedBookings.length}
                    </span>
                  </span>
                  <div className="action-group-description">Bookings awaiting operator assignment</div>
                </div>
              </div>
              <div className="action-group-items">
                {bookingsData.unassignedBookings.slice(0, 5).map(booking => {
                  const bouncingTooltip = booking.email_bouncing
                    ? (() => {
                        const addr = booking.customer_email ? ` (${booking.customer_email})` : '';
                        if (!booking.email_last_bounce_at) return `Customer's email${addr} is bouncing. Booking emails won't deliver until the address is fixed.`;
                        const days = Math.floor((Date.now() - new Date(booking.email_last_bounce_at).getTime()) / 86_400_000);
                        const when = days <= 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
                        return `Customer's email${addr} last bounced ${when}. Booking emails won't deliver until the address is fixed.`;
                      })()
                    : '';
                  return (
                  <div
                    key={booking.id}
                    className="action-item action-item-compact"
                    onClick={() => navigate(`/bookings?view=${booking.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="action-item-content-inline">
                      <span className="action-item-title-inline">{formatBookingDate(booking)} {formatBookingTime(booking)}</span>
                      <span className="action-item-separator">&middot;</span>
                      <span className="action-item-subtitle-inline">
                        {booking.customer_name} &middot; {booking.reference}
                        {booking.email_bouncing && (
                          <span
                            title={bouncingTooltip}
                            aria-label="Email bouncing"
                            style={{ marginLeft: '6px', fontSize: 'var(--text-base)', cursor: 'help' }}
                          >
                            ⚠️
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="row-actions">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/bookings?view=${booking.id}&assign=true`); }}
                        className="btn btn-action btn-primary"
                        style={{ minWidth: '80px' }}
                      >
                        Assign
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
              {bookingsData.unassignedBookings.length > 5 && (
                <Link to="/bookings?filter=unassigned" className="action-group-more">
                  +{bookingsData.unassignedBookings.length - 5} more
                </Link>
              )}
            </div>
          )}
        </DashboardSection>
      )}

      {/* Service Logs Section — hidden when empty */}
      {serviceLogsCount > 0 && (
        <DashboardSection
          icon="📋" title="Service Logs" count={serviceLogsCount}
          accentColor="var(--color-primary)" loading={serviceLogsData.loading}
          headerAction={<Link to="/service-logs" className="dashboard-section-link">View All</Link>}
        >
          <div className="flex flex-col gap-lg">
            {serviceLogsData.pending.length > 0 && showPendingApprovals && (
              <ActionGroup
                title="Pending Approval"
                description="Service completed - create draft invoice"
                count={serviceLogsData.pending.length}
                color="var(--color-warning)"
                items={serviceLogsData.pending.slice(0, 5)}
                renderItem={(item) => {
                  const log = item as unknown as WorkflowItem;
                  return {
                    title: log.customer_name || 'Unknown',
                    subtitle: `${formatDate(log.service_date)}${log.split_code?.code ? ` · ${log.split_code.description || log.split_code.code}` : ''} · ${formatCurrency(log.total_amount || 0)}`,
                    hasNotes: !!log.internal_notes,
                    isEmailBouncing: log.email_bouncing === true,
                    emailLastBounceAt: log.email_last_bounce_at ?? null,
                    emailAddress: log.customer_email,
                  };
                }}
                actionLabel="Approve"
                onAction={(item) => handleApproveServiceLog(item as unknown as WorkflowItem)}
                onView={(item) => handleViewServiceLog(item as unknown as WorkflowItem)}
                onEdit={(item) => handleEditServiceLog(item as unknown as WorkflowItem)}
                showEdit={() => canEditServiceLogs}
                viewAllHref="/service-logs?status=pending"
                processingItemId={processingItemId}
              />
            )}
            {serviceLogsData.draft.length > 0 && canSendInvoice && (
              <ActionGroup
                title="Ready to Send"
                description="Invoice created - send to customer"
                count={serviceLogsData.draft.length}
                color="var(--color-primary)"
                items={serviceLogsData.draft.slice(0, 5)}
                renderItem={(item) => {
                  const log = item as unknown as WorkflowItem;
                  return {
                    title: log.customer_name || 'Unknown',
                    subtitle: `${log.invoice?.invoice_number || '—'}${log.split_code?.code ? ` · ${log.split_code.description || log.split_code.code}` : ''} · ${formatCurrency(log.total_amount || 0)}`,
                    hasNotes: !!log.internal_notes,
                    isEmailBouncing: log.email_bouncing === true,
                    emailLastBounceAt: log.email_last_bounce_at ?? null,
                    emailAddress: log.customer_email,
                  };
                }}
                actionLabel="Send"
                onAction={(item) => handleSendInvoice(item as unknown as WorkflowItem)}
                onView={(item) => handleViewServiceLog(item as unknown as WorkflowItem)}
                onEdit={(item) => handleEditServiceLog(item as unknown as WorkflowItem)}
                showEdit={() => canEditServiceLogs}
                viewAllHref="/service-logs?status=draft"
                processingItemId={processingItemId}
              />
            )}
          </div>
        </DashboardSection>
      )}

      {/* Certification Section — hidden when empty */}
      {certificationCount > 0 && (
        <DashboardSection
          icon="📜" title="Certification" count={certificationCount}
          accentColor="#16a34a" loading={certificationData.loading}
          headerAction={<Link to="/certification" className="dashboard-section-link">View All</Link>}
        >
          <div className="flex flex-col gap-lg">
            {certificationData.pendingVerification.length > 0 && canVerify && (
              <ActionGroup
                title="Needs Verification"
                description="Certificate submitted - verify service details for authorisation"
                count={certificationData.pendingVerification.length}
                color="var(--color-primary)"
                items={certificationData.pendingVerification.slice(0, 5)}
                renderItem={(item) => {
                  const cert = item as unknown as CertItem;
                  return { title: cert.display_customer, subtitle: `${formatDate(cert.service_date)} · By: ${cert.operator_name}` };
                }}
                actionLabel="Verify"
                onAction={(item) => handleVerifySubmission(item as unknown as CertItem)}
                onView={(item) => handleViewCertSubmission(item as unknown as CertItem)}
                showEdit={() => false}
                viewAllHref="/certification?status=pending_verification"
                processingItemId={processingItemId}
              />
            )}
            {certificationData.pendingAuthorization.length > 0 && canAuthorize && (
              <ActionGroup
                title="Needs Authorisation"
                description="Certificate verified - authorise certificate for issuance"
                count={certificationData.pendingAuthorization.length}
                color="var(--color-primary-dark)"
                items={certificationData.pendingAuthorization.slice(0, 5)}
                renderItem={(item) => {
                  const cert = item as unknown as CertItem;
                  return { title: cert.display_customer, subtitle: `${formatDate(cert.service_date)} · ${cert.invoice_number || '—'}` };
                }}
                actionLabel="Authorise"
                onAction={(item) => handleAuthoriseSubmission(item as unknown as CertItem)}
                onView={(item) => handleViewCertSubmission(item as unknown as CertItem)}
                showEdit={() => false}
                viewAllHref="/certification?status=pending_authorization"
                processingItemId={processingItemId}
              />
            )}
          </div>
        </DashboardSection>
      )}

      {/* Quick Actions Card */}
      <div className="dashboard-section quick-actions-card">
        <div className="dashboard-section-header">
          <h3 className="dashboard-section-title">
            <span className="dashboard-section-icon">&#9889;</span>
            Quick Actions
          </h3>
        </div>
        <div className="dashboard-section-body">
          <div className="quick-actions-grid">
            {canAccessRewards ? (
              <button className="quick-action-tile" onClick={() => setShowSpeedDial(true)}>
                <span className="quick-action-icon">🎁</span>
                <span className="quick-action-label">Reward</span>
                <span className="quick-action-desc">Voucher or Referral</span>
              </button>
            ) : (
              <button className="quick-action-tile quick-action-disabled" disabled>
                <span className="quick-action-icon">🎁</span>
                <span className="quick-action-label">Reward</span>
                <span className="quick-action-desc">No access</span>
              </button>
            )}
            {canCreateBooking ? (
              <Link to="/bookings?create=true" className="quick-action-tile">
                <span className="quick-action-icon">📅</span>
                <span className="quick-action-label">Book</span>
                <span className="quick-action-desc">Create booking</span>
              </Link>
            ) : (
              <button className="quick-action-tile quick-action-disabled" disabled>
                <span className="quick-action-icon">📅</span>
                <span className="quick-action-label">Book</span>
                <span className="quick-action-desc">No access</span>
              </button>
            )}
            <Link to="/service-logs/new" className="quick-action-tile">
              <span className="quick-action-icon">📋</span>
              <span className="quick-action-label">Log</span>
              <span className="quick-action-desc">Create service log</span>
            </Link>
            <Link to="/certification/submit" className="quick-action-tile">
              <span className="quick-action-icon">📜</span>
              <span className="quick-action-label">Cert</span>
              <span className="quick-action-desc">Submit certificate</span>
            </Link>
          </div>
        </div>
      </div>

      {/* View Actions Card */}
      <div className="dashboard-section quick-actions-card">
        <div className="dashboard-section-header">
          <h3 className="dashboard-section-title">
            <span className="dashboard-section-icon">📂</span>
            View
          </h3>
        </div>
        <div className="dashboard-section-body">
          <div className="quick-actions-grid">
            {canAccessRewards ? (
              <Link to={rewardsViewPath} className="quick-action-tile">
                <span className="quick-action-icon">🎁</span>
                <span className="quick-action-label">Rewards</span>
                <span className="quick-action-desc">View rewards</span>
              </Link>
            ) : (
              <button className="quick-action-tile quick-action-disabled" disabled>
                <span className="quick-action-icon">🎁</span>
                <span className="quick-action-label">Rewards</span>
                <span className="quick-action-desc">No access</span>
              </button>
            )}
            <Link to="/bookings" className="quick-action-tile">
              <span className="quick-action-icon">📅</span>
              <span className="quick-action-label">Bookings</span>
              <span className="quick-action-desc">View bookings</span>
            </Link>
            <Link to="/service-logs" className="quick-action-tile">
              <span className="quick-action-icon">📋</span>
              <span className="quick-action-label">Logs</span>
              <span className="quick-action-desc">View service logs</span>
            </Link>
            <Link to="/certification" className="quick-action-tile">
              <span className="quick-action-icon">📜</span>
              <span className="quick-action-label">Certs</span>
              <span className="quick-action-desc">View certificates</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Reward Speed Dial */}
      {showSpeedDial && (
        <RewardSpeedDial
          onClose={() => setShowSpeedDial(false)}
          onSelectVoucher={() => { setShowSpeedDial(false); setShowVoucherModal(true); }}
          onSelectReferral={() => { setShowSpeedDial(false); setShowReferralModal(true); }}
        />
      )}

      {showVoucherModal && <MobileCreateVoucherModal onClose={() => setShowVoucherModal(false)} onSuccess={() => setShowVoucherModal(false)} />}
      {showReferralModal && <MobileReferralShareModal onClose={() => setShowReferralModal(false)} />}


      {/* Send Invoice Modal */}
      {showSendModal && sendingInvoice && (
        <SendInvoiceModal
          invoice={sendingInvoice}
          onClose={() => { setShowSendModal(false); setSendingInvoice(null); }}
          onSuccess={handleSendSuccess}
        />
      )}

      {/* Approve Service Log Modal */}
      {approvingLog && (
        <ApproveServiceLogModal
          log={{
            id: approvingLog.id,
            customer_name: approvingLog.customer_name,
            customer_email: approvingLog.customer_email,
            total_amount: approvingLog.total_amount,
            service_date: approvingLog.service_date,
          }}
          onClose={() => setApprovingLog(null)}
          onConfirm={(overrides) => executeApproveServiceLog(approvingLog, overrides)}
        />
      )}

      {/* Service Log View Modal */}
      {viewingServiceLog && (
        <ServiceLogViewModal
          log={viewingServiceLog as unknown as { status: string; [key: string]: unknown }}
          onClose={() => setViewingServiceLog(null)}
          onEdit={(log) => { setViewingServiceLog(null); handleEditServiceLog(log as unknown as WorkflowItem); }}
          onApprove={(log) => { setViewingServiceLog(null); handleApproveServiceLog(log as unknown as WorkflowItem); }}
          onSend={(log) => { setViewingServiceLog(null); handleSendInvoice(log as unknown as WorkflowItem); }}
          canEdit={canEditServiceLogs}
          canApprove={canApproveServiceLogs}
          canSend={canSendInvoice}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      )}

      {/* Certification Submission View Modal */}
      {viewingCertSubmission && (
        <CertSubmissionViewModal
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          submission={viewingCertSubmission as any}
          onClose={() => setViewingCertSubmission(null)}
          onVerify={(sub) => { setViewingCertSubmission(null); handleVerifySubmission(sub as unknown as CertItem); }}
          onAuthorize={(sub) => { setViewingCertSubmission(null); handleAuthoriseSubmission(sub as unknown as CertItem); }}
          canVerify={canVerify}
          canAuthorize={canAuthorize}
          formatDate={formatDate}
        />
      )}

      {/* Invoice Manager overlay */}
      {editingInvoiceNumber && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'var(--color-bg, #f8f9fa)', zIndex: 1000, overflow: 'auto'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
            <button className="btn btn-secondary mb-lg" onClick={handleCloseInvoiceManager}>
              &larr; Back to Dashboard
            </button>
            <InvoiceManager initialInvoiceNumber={editingInvoiceNumber} />
          </div>
        </div>
      )}

      {/* Speed Dial FAB */}
      <SpeedDialFAB
        actions={[
          { label: 'Add Customer', icon: '👤', onClick: () => navigate('/customers?action=add') },
          { label: 'Add Lead', icon: '📋', onClick: () => navigate('/leads?action=add') }
        ]}
      />
    </div>
  );
}

// Dashboard Section Component
interface DashboardSectionProps {
  icon: string;
  title: string;
  count: number;
  accentColor: string;
  loading: boolean;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

function DashboardSection({ icon, title, count, accentColor, loading, headerAction, children }: DashboardSectionProps) {
  return (
    <div className="dashboard-section" style={{ '--section-accent': accentColor } as React.CSSProperties}>
      <div className="dashboard-section-header">
        <h3 className="dashboard-section-title">
          <span className="dashboard-section-icon">{icon}</span>
          {title}
          {count > 0 && (
            <span className="dashboard-section-badge" style={{ background: accentColor }}>
              {count}
            </span>
          )}
        </h3>
        {headerAction}
      </div>
      <div className="dashboard-section-body">
        {loading ? (
          <p className="dashboard-empty-message">Loading...</p>
        ) : count === 0 ? (
          <p className="dashboard-empty-message">All clear</p>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// Action Group Component — uses generic item type to avoid union issues
interface ActionGroupItem {
  id: string;
  [key: string]: unknown;
}

interface ActionGroupProps {
  title: string;
  description?: string;
  count: number;
  color: string;
  items: ActionGroupItem[];
  renderItem: (item: ActionGroupItem) => { title: string; subtitle: string; hasNotes?: boolean; isEmailBouncing?: boolean; emailLastBounceAt?: string | null; emailAddress?: string };
  actionLabel: string;
  onAction: (item: ActionGroupItem) => void;
  onView?: (item: ActionGroupItem) => void;
  onEdit?: (item: ActionGroupItem) => void;
  showEdit?: (item: ActionGroupItem) => boolean;
  viewAllHref: string;
  processingItemId: string | null;
}

function ActionGroup({ title, description, count, color, items, renderItem, actionLabel, onAction, onView, onEdit, showEdit, viewAllHref, processingItemId }: ActionGroupProps) {
  return (
    <div className="action-group" style={{ borderLeftColor: color }}>
      <div className="action-group-header">
        <div>
          <span className="action-group-title">
            {title}
            <span className="action-group-badge" style={{ background: color }}>
              {count}
            </span>
          </span>
          {description && <div className="action-group-description">{description}</div>}
        </div>
        {count > 5 && (
          <Link to={viewAllHref} className="action-group-more">
            +{count - 5} more
          </Link>
        )}
      </div>
      <div className="action-group-items">
        {items.map(item => {
          const { title: itemTitle, subtitle, hasNotes, isEmailBouncing, emailLastBounceAt, emailAddress } = renderItem(item);
          const canEdit = showEdit ? showEdit(item) : false;
          const isProcessing = processingItemId === item.id;
          const bouncingTooltip = isEmailBouncing
            ? (() => {
                const addr = emailAddress ? ` (${emailAddress})` : '';
                if (!emailLastBounceAt) return `Customer's email${addr} is bouncing. Sending the invoice will not deliver until the address is fixed.`;
                const days = Math.floor((Date.now() - new Date(emailLastBounceAt).getTime()) / 86_400_000);
                const when = days <= 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
                return `Customer's email${addr} last bounced ${when}. Sending the invoice will not deliver until the address is fixed.`;
              })()
            : '';
          return (
            <div
              key={item.id}
              className="action-item action-item-compact"
              onClick={() => onView?.(item)}
              style={{ cursor: onView ? 'pointer' : 'default', opacity: isProcessing ? 0.7 : 1 }}
            >
              <div className="action-item-content-inline">
                <span className="action-item-title-inline">
                  {itemTitle}
                  {hasNotes && <span title="Has notes" style={{ marginLeft: '4px' }}>📝</span>}
                  {isEmailBouncing && (
                    <span
                      title={bouncingTooltip}
                      aria-label="Email bouncing"
                      style={{ marginLeft: '6px', fontSize: 'var(--text-base)', cursor: 'help' }}
                    >
                      ⚠️
                    </span>
                  )}
                </span>
                <span className="action-item-separator">&middot;</span>
                <span className="action-item-subtitle-inline">{subtitle}</span>
              </div>
              <div className="row-actions">
                <span
                  className="action-icon"
                  onClick={(e) => { e.stopPropagation(); onView?.(item); }}
                  title="View"
                >
                  👁️
                </span>
                <span
                  className={`action-icon ${!(canEdit && onEdit) ? 'hidden' : ''}`}
                  onClick={(e) => { if (canEdit && onEdit) { e.stopPropagation(); onEdit(item); }}}
                  title="Edit"
                >
                  ✏️
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); if (!isProcessing) onAction(item); }}
                  className="btn btn-action btn-primary"
                  disabled={isProcessing}
                  style={{ minWidth: '80px' }}
                >
                  {isProcessing ? 'Processing...' : actionLabel}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UserHome;
